import { useCallback, useRef, type PointerEvent as ReactPointerEvent } from 'react';

// How a finger or a mouse selects bars on a sheet (Oct 2026, user: selecting
// bars to loop was "fiddly because it enters full screen and focus mode").
// A TAP on a bar is the start cursor (the sheet's own click handler); this
// hook owns the DRAG that selects bars:
//
// - A mouse drags: press on a bar and move, in any direction.
// - A finger HOLDS first (iReal's gesture): rest on a bar for HOLD_MS and the
//   bar is selected, then drag to extend across bars and lines. A finger that
//   moves before then is scrolling the page and is left alone, which is why a
//   swipe can no longer start a selection by accident.
// - Shift-click extends the selection.
//
// The selection a drag or hold makes is not a tap, so the click that may
// follow it is swallowed (`clickIsGesture`) — that click only: the next press
// is a new gesture, and a tap straight after a shift-click is a tap.

/** How long a finger rests on a bar before dragging selects bars. */
export const HOLD_MS = 400;
/** Movement that makes a press a drag (mouse) or a scroll (touch, before the hold). */
export const MOVE_SLOP = 8;

const CONTROL_SELECTOR = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  'label',
  '[role="button"]',
  '[role="link"]',
  '[role="slider"]',
  '[role="checkbox"]',
  '[contenteditable="true"]',
].join(',');

/**
 * Did the press land on a control drawn on the sheet (a chord diagram, a
 * playable chord symbol) rather than on the paper? Walks up from the target
 * and stops at `surface`, so the surface's own role never counts.
 */
export function onControl(target: EventTarget | null, surface: Element | null): boolean {
  for (
    let node = target instanceof Element ? target : null;
    node && node !== surface;
    node = node.parentElement
  ) {
    if (node.matches(CONTROL_SELECTOR)) return true;
  }
  return false;
}

type Phase = 'pending' | 'dragging' | 'holding';

interface Gesture {
  pointerId: number;
  touch: boolean;
  phase: Phase;
  from: string;
  last: string;
  x: number;
  y: number;
  element: Element;
}

export interface PassageGestureOptions {
  onSelectPassage?: (fromId: string, toId: string, opts?: { extend?: boolean }) => void;
  onPassageGesture?: () => void;
}

export function usePassageGesture(options: PassageGestureOptions) {
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const gesture = useRef<Gesture | null>(null);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressUntil = useRef(0);
  const surface = useRef<Element | null>(null);

  const clearTimer = () => {
    if (holdTimer.current !== null) clearTimeout(holdTimer.current);
    holdTimer.current = null;
  };
  const finish = () => {
    clearTimer();
    gesture.current = null;
    optionsRef.current.onPassageGesture?.();
    suppressUntil.current = performance.now() + 700;
  };

  /** A press on a bar (`id`), or on nothing a drag could start from. */
  const down = (event: ReactPointerEvent<Element>, id: string | null) => {
    // A new press: any click still to come belongs to it, not to the last
    // gesture.
    suppressUntil.current = 0;
    const select = optionsRef.current.onSelectPassage;
    if (!select || event.button !== 0 || event.isPrimary === false) {
      // A second finger is a pinch, never a selection.
      if (event.isPrimary === false && gesture.current) {
        clearTimer();
        gesture.current = null;
      }
      return;
    }
    if (!id) return;
    const touch = event.pointerType === 'touch';
    // A held chord diagram strums: its hold is its own.
    if (touch && onControl(event.target, event.currentTarget)) return;
    clearTimer();
    gesture.current = {
      pointerId: event.pointerId,
      touch,
      phase: 'pending',
      from: id,
      last: id,
      x: event.clientX,
      y: event.clientY,
      element: event.currentTarget,
    };
    if (touch) {
      holdTimer.current = setTimeout(() => {
        holdTimer.current = null;
        const g = gesture.current;
        if (!g || g.phase !== 'pending') return;
        g.phase = 'holding';
        try {
          g.element.setPointerCapture(g.pointerId);
        } catch {
          /* the pointer may have gone */
        }
        navigator.vibrate?.(10);
        optionsRef.current.onSelectPassage?.(g.from, g.from);
      }, HOLD_MS);
    } else {
      // Keep a mouse drag from selecting the sheet's text.
      event.preventDefault();
    }
  };

  /** The pointer moved; `idAt` names the bar under it. */
  const move = (event: ReactPointerEvent<Element>, idAt: () => string | null) => {
    const g = gesture.current;
    if (!g || event.pointerId !== g.pointerId) return;
    if (g.phase === 'pending') {
      if (Math.hypot(event.clientX - g.x, event.clientY - g.y) < MOVE_SLOP) return;
      if (g.touch) {
        // Moving before the hold: the finger is scrolling the page.
        clearTimer();
        gesture.current = null;
        return;
      }
      g.phase = 'dragging';
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        /* not capturable in this environment */
      }
    }
    const id = idAt();
    if (!id) return;
    g.last = id;
    optionsRef.current.onSelectPassage?.(g.from, id);
  };

  const up = (event: ReactPointerEvent<Element>) => {
    const g = gesture.current;
    if (!g || event.pointerId !== g.pointerId) return;
    clearTimer();
    if (g.phase !== 'pending') {
      optionsRef.current.onSelectPassage?.(g.from, g.last);
      finish();
      return;
    }
    gesture.current = null;
    if (event.shiftKey) {
      optionsRef.current.onSelectPassage?.(g.from, g.from, { extend: true });
      finish();
    }
  };

  const cancel = () => {
    const g = gesture.current;
    clearTimer();
    if (g && g.phase !== 'pending') finish();
    else gesture.current = null;
  };

  /** True for the click that ends a drag, a hold or a shift-click: it is not a
   * tap. Until the next press, and for 700ms at most. */
  const clickIsGesture = () => performance.now() < suppressUntil.current;

  // While a finger holds and drags, the page must not scroll under it. Only a
  // listener that is not passive can stop that, and React's touch listeners
  // are passive, so the surface gets native ones. A long press would also
  // open the context menu on Android.
  const onTouchMove = useCallback((event: Event) => {
    if (gesture.current?.phase === 'holding') event.preventDefault();
  }, []);
  const onContextMenu = useCallback((event: Event) => {
    if (gesture.current?.touch) event.preventDefault();
  }, []);
  const surfaceRef = useCallback(
    (element: Element | null) => {
      const previous = surface.current;
      if (previous === element) return;
      if (previous) {
        previous.removeEventListener('touchmove', onTouchMove);
        previous.removeEventListener('contextmenu', onContextMenu);
      }
      surface.current = element;
      if (element) {
        element.addEventListener('touchmove', onTouchMove, { passive: false });
        element.addEventListener('contextmenu', onContextMenu);
      }
    },
    [onTouchMove, onContextMenu],
  );

  return { down, move, up, cancel, clickIsGesture, surfaceRef };
}
